import { PredictionArtifact } from "./types";

const REQUIRED_TOP_LEVEL_FIELDS = [
  "schema_version",
  "schema_compatibility",
  "run_id",
  "data_as_of_utc",
  "artifact_generated_at_utc",
  "snapshot_version",
  "artifact_type",
  "run_status",
  "last_updated_at",
  "run_context",
  "summary",
  "predictions",
  "withheld_games",
  "failures",
  "producer",
] as const;

const ALLOWED_TOP_LEVEL_FIELDS = new Set<string>([
  ...REQUIRED_TOP_LEVEL_FIELDS,
  "warnings",
  "run_metrics",
  "scheduled_games",
]);

const REQUIRED_PREDICTION_FIELDS = [
  "game_id",
  "model_id",
  "model_source",
  "game_date",
  "game_start_time_utc",
  "game_type",
  "winner",
  "winner_id",
  "final_confidence",
  "confidence_score_percent",
  "feature_completeness",
  "home_team",
  "home_team_id",
  "away_team",
  "away_team_id",
  "predicted_home_score",
  "predicted_away_score",
  "predicted_spread",
  "predicted_total",
  "home_win_prob",
  "calibration_status",
  "lineups_confirmed_at_run",
  "cadence_bucket_at_run",
  "minutes_to_first_pitch_at_run",
  "selection_reason",
  "home_starting_lineup",
  "away_starting_lineup",
] as const;

const ALLOWED_PREDICTION_FIELDS = new Set<string>([
  ...REQUIRED_PREDICTION_FIELDS,
  "home_team_city",
  "away_team_city",
  "home_team_location",
  "away_team_location",
  "venue_time_zone",
  "home_starting_pitcher",
  "away_starting_pitcher",
  "predicted_winner",
  "lineup_confirmed_at_utc",
  "win_confidence",
  "calibrated_confidence",
  "feature_completeness",
  "meta_confidence",
  "tree_agreement",
  "historical_support",
  "similar_games_count",
  "matching_outcomes",
  "confidence_level",
  "confidence_stars",
  "actual_home_score",
  "actual_away_score",
  "actual_winner",
  "prediction_correct",
  "lineups_confirmed",
  "game_status",
  "status_detailed",
]);

const REQUIRED_WITHHELD_FIELDS = [
  "game_id",
  "game_date",
  "game_start_time_utc",
  "game_type",
  "lineups_confirmed_at_run",
  "home_starting_pitcher_id_present",
  "away_starting_pitcher_id_present",
  "home_lineup_slot_count",
  "away_lineup_slot_count",
  "named_starters_present",
  "readiness_reason_codes",
  "status_message_code",
  "minutes_to_first_pitch_at_run",
  "cadence_bucket_at_run",
  "selection_reason",
  "status_abstract",
  "status_detailed",
] as const;

const ALLOWED_WITHHELD_FIELDS = new Set<string>([
  ...REQUIRED_WITHHELD_FIELDS,
  "home_team",
  "away_team",
  "home_team_id",
  "away_team_id",
  "venue_time_zone",
  "lineup_confirmed_at_utc",
]);

const REQUIRED_SCHEDULED_GAME_FIELDS = [
  "game_id",
  "game_date",
  "game_start_time_utc",
  "game_type",
  "status_abstract",
  "status_detailed",
  "status_reason",
  "lineups_confirmed",
  "named_starters_present",
  "home_lineup_slot_count",
  "away_lineup_slot_count",
] as const;

const ALLOWED_SCHEDULED_GAME_FIELDS = new Set<string>([
  ...REQUIRED_SCHEDULED_GAME_FIELDS,
  "home_team",
  "away_team",
  "home_team_id",
  "away_team_id",
  "venue_time_zone",
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasRequiredFields(obj: Record<string, unknown>, required: readonly string[], context: string): void {
  const missing = required.filter((key) => !(key in obj));
  if (missing.length > 0) {
    throw new Error(`Artifact schema validation failed (${context} missing: ${missing.join(", ")})`);
  }
}

function assertPredictionShape(prediction: Record<string, unknown>, idx: number): void {
  hasRequiredFields(prediction, REQUIRED_PREDICTION_FIELDS, `prediction[${idx}]`);

  const extraFields = Object.keys(prediction).filter((key) => !ALLOWED_PREDICTION_FIELDS.has(key));
  if (extraFields.length > 0) {
    throw new Error(
      `Artifact schema validation failed (prediction[${idx}] has undeclared fields: ${extraFields.join(", ")})`,
    );
  }
}

function assertWithheldShape(withheld: Record<string, unknown>, idx: number): void {
  hasRequiredFields(withheld, REQUIRED_WITHHELD_FIELDS, `withheld_games[${idx}]`);

  const extraFields = Object.keys(withheld).filter((key) => !ALLOWED_WITHHELD_FIELDS.has(key));
  if (extraFields.length > 0) {
    throw new Error(
      `Artifact schema validation failed (withheld_games[${idx}] has undeclared fields: ${extraFields.join(", ")})`,
    );
  }
}

function assertScheduledGameShape(game: Record<string, unknown>, idx: number): void {
  hasRequiredFields(game, REQUIRED_SCHEDULED_GAME_FIELDS, `scheduled_games[${idx}]`);

  const extraFields = Object.keys(game).filter((key) => !ALLOWED_SCHEDULED_GAME_FIELDS.has(key));
  if (extraFields.length > 0) {
    throw new Error(
      `Artifact schema validation failed (scheduled_games[${idx}] has undeclared fields: ${extraFields.join(", ")})`,
    );
  }
}

export function validateStrictPredictionArtifact(payload: unknown): PredictionArtifact {
  if (!isObject(payload)) {
    throw new Error("Artifact schema validation failed (payload must be an object)");
  }

  hasRequiredFields(payload, REQUIRED_TOP_LEVEL_FIELDS, "top-level");

  const topLevelExtras = Object.keys(payload).filter((key) => !ALLOWED_TOP_LEVEL_FIELDS.has(key));
  if (topLevelExtras.length > 0) {
    throw new Error(
      `Artifact schema validation failed (top-level has undeclared fields: ${topLevelExtras.join(", ")})`,
    );
  }

  if (payload.schema_compatibility !== "strict_v1") {
    throw new Error(
      `Artifact schema validation failed (unsupported schema_compatibility: ${String(payload.schema_compatibility)})`,
    );
  }

  if (typeof payload.schema_version !== "string" || !payload.schema_version.startsWith("1.")) {
    throw new Error(
      `Artifact schema validation failed (unsupported schema_version: ${String(payload.schema_version)})`,
    );
  }

  if (!Array.isArray(payload.predictions)) {
    throw new Error("Artifact schema validation failed (predictions must be an array)");
  }

  if (!Array.isArray(payload.withheld_games)) {
    throw new Error("Artifact schema validation failed (withheld_games must be an array)");
  }

  payload.predictions.forEach((row, idx) => {
    if (!isObject(row)) {
      throw new Error(`Artifact schema validation failed (prediction[${idx}] must be an object)`);
    }
    assertPredictionShape(row, idx);
  });

  payload.withheld_games.forEach((row, idx) => {
    if (!isObject(row)) {
      throw new Error(`Artifact schema validation failed (withheld_games[${idx}] must be an object)`);
    }
    assertWithheldShape(row, idx);
  });

  if (Array.isArray(payload.scheduled_games)) {
    payload.scheduled_games.forEach((row, idx) => {
      if (!isObject(row)) {
        throw new Error(`Artifact schema validation failed (scheduled_games[${idx}] must be an object)`);
      }
      assertScheduledGameShape(row, idx);
    });
  }

  return payload as PredictionArtifact;
}
